/**
 * Market watch Redux slice — list CRUD, indices, thunks.
 * External HTTP (login key / bbdterminal) is delegated to services/externalMarketFeeds.js.
 */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "utils/api";
import { fetchLoginKeyRawText, fetchBbdTerminalJson } from "services/externalMarketFeeds";
import { showMessage } from "./common.slice";
import _ from "lodash";
import moment from "moment-timezone";
import { getSessionData } from "utils/common";
import { isSessionTradingAllowed } from "utils/sessionRole";
import { checkIsEmpty } from "utils/common";
import { log, errorLog } from "utils/logger";
import { normalizeReduxListPayload } from "utils/normalizeReduxList";
import { bootstrapIndexRowFromApi } from "utils/indexStripRows";
import { legacyAxiosToastMessage } from "utils/httpErrors";

function Search(searchText, dataArray) {
  const q = (searchText || "").trim().toLowerCase();
  const list = Array.isArray(dataArray) ? dataArray : [];
  if (!q) return list;
  return list.filter((x) => {
    if (!x) return false;
    if (typeof x === "string") return x.toLowerCase().includes(q);
    const haystack = [x.displayName, x.script, x.symbol, x.name, x.label, x.exchange]
      .filter(Boolean).join(" ").toLowerCase();
    return haystack.includes(q);
  });
}

const getItemKey = (x) => {
  if (x == null) return "";
  if (x.id != null && x.id !== "") return `id:${x.id}`;
  const k = x.displayName || x.script || x.symbol || x.name || x.label;
  return k ? String(k) : "";
};

const MW_ORDER_LS_PREFIX = "fxbazzar_mw_order";

// Persist last-known ticks so refresh (esp. after market close) can show previous values.
const TICK_CACHE_PREFIX = "fxbazzar_tick_cache";
function tickCacheStorageKey(userId) {
  return userId ? `${TICK_CACHE_PREFIX}_${userId}` : `${TICK_CACHE_PREFIX}_anon`;
}
function normalizeTickKey(v) {
  return String(v ?? "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "");
}
function loadTickCache(userId) {
  try {
    const raw = localStorage.getItem(tickCacheStorageKey(userId));
    if (!raw) return {};
    const obj = JSON.parse(raw);
    return obj && typeof obj === "object" ? obj : {};
  } catch {
    return {};
  }
}
function cacheKeysForRow(row) {
  if (!row || typeof row !== "object") return [];
  const out = new Set();
  const add = (x) => {
    const k = normalizeTickKey(x);
    if (k) out.add(k);
    const base = k.split(/[0-9]/)[0]; // fallback; cheap base shrink for expiry-heavy names
    if (base && base.length >= 4) out.add(base);
    const first = String(x ?? "").trim().split(/\s+/)[0];
    const kFirst = normalizeTickKey(first);
    if (kFirst) out.add(kFirst);
  };
  add(row.displayName);
  add(row.script);
  add(row.symbol);
  add(row.name);
  add(row.Script);
  add(row.Symbol);
  return Array.from(out);
}
function mergeRowWithTickCache(apiRow, cache) {
  if (!apiRow || typeof apiRow !== "object" || !cache) return apiRow;
  const apiLtp = Number(apiRow.lastTradedPrice ?? apiRow.ltp ?? apiRow.LTP ?? 0);
  const apiBid = Number(apiRow.buyPrice ?? apiRow.bid ?? 0);
  const apiAsk = Number(apiRow.sellPrice ?? apiRow.ask ?? 0);
  const needs =
    !Number.isFinite(apiLtp) || apiLtp <= 0 || !Number.isFinite(apiBid) || apiBid <= 0 || !Number.isFinite(apiAsk) || apiAsk <= 0;
  if (!needs) return apiRow;

  for (const k of cacheKeysForRow(apiRow)) {
    const hit = cache[k];
    if (!hit || typeof hit !== "object") continue;
    const ltp = Number(hit.ltp ?? 0);
    if (!Number.isFinite(ltp) || ltp <= 0) continue;
    return {
      ...apiRow,
      lastTradedPrice: apiLtp > 0 ? apiRow.lastTradedPrice : ltp,
      buyPrice: apiBid > 0 ? apiRow.buyPrice : Number(hit.bid ?? apiBid ?? 0),
      sellPrice: apiAsk > 0 ? apiRow.sellPrice : Number(hit.ask ?? apiAsk ?? 0),
      close:
        Number(apiRow.close ?? 0) > 0
          ? apiRow.close
          : Number(hit.prevClose ?? hit.close ?? apiRow.close ?? 0),
      __stablePrevClose:
        Number(apiRow.__stablePrevClose ?? 0) > 0
          ? apiRow.__stablePrevClose
          : Number(hit.prevClose ?? hit.close ?? apiRow.__stablePrevClose ?? 0),
      changeAmount: hit.changeAmount ?? apiRow.changeAmount,
      changePercent: hit.changePercent ?? apiRow.changePercent,
      changeAmountColor: hit.changeAmountColor ?? apiRow.changeAmountColor,
    };
  }
  return apiRow;
}

/** Restore watchlist row order from localStorage (per user) after API load. */
function applySavedWatchlistOrder(list, userId) {
  if (!userId || !Array.isArray(list) || list.length === 0) return list;
  try {
    const raw = localStorage.getItem(`${MW_ORDER_LS_PREFIX}_${userId}`);
    if (!raw) return list;
    const idOrder = JSON.parse(raw);
    if (!Array.isArray(idOrder) || idOrder.length === 0) return list;
    const out = [];
    idOrder.forEach((id) => {
      const row = list.find((r) => r != null && Number(r.id) === Number(id));
      if (row && !out.includes(row)) out.push(row);
    });
    list.forEach((r) => {
      if (r && !out.includes(r)) out.push(r);
    });
    return out.length === list.length ? out : list;
  } catch {
    return list;
  }
}

/**
 * Watchlist API often returns LTP/Bid/Ask as 0 while live quotes exist only via socket.
 * Merging prevents a full refresh from wiping prices until the next tick.
 */
function mergeWatchlistRowWithPrevious(prevRow, apiRow) {
  if (!apiRow) return prevRow;
  if (!prevRow) return apiRow;
  const sameId =
    prevRow.id != null &&
    apiRow.id != null &&
    String(prevRow.id) === String(apiRow.id);
  if (!sameId) return apiRow;
  const apiLtp = Number(apiRow.lastTradedPrice ?? apiRow.ltp ?? apiRow.LTP ?? 0);
  const prevLtp = Number(prevRow.lastTradedPrice ?? prevRow.ltp ?? prevRow.LTP ?? 0);
  const apiBid = Number(apiRow.buyPrice ?? apiRow.bid ?? 0);
  const prevBid = Number(prevRow.buyPrice ?? prevRow.bid ?? 0);
  const apiAsk = Number(apiRow.sellPrice ?? apiRow.ask ?? 0);
  const prevAsk = Number(prevRow.sellPrice ?? prevRow.ask ?? 0);
  const keepLtp = prevLtp > 0 && (!Number.isFinite(apiLtp) || apiLtp === 0);
  const keepBid = prevBid > 0 && (!Number.isFinite(apiBid) || apiBid === 0);
  const keepAsk = prevAsk > 0 && (!Number.isFinite(apiAsk) || apiAsk === 0);
  if (!keepLtp && !keepBid && !keepAsk) return apiRow;
  const numOrPrev = (apiVal, prevVal) => {
    const a = Number(apiVal ?? 0);
    return Number.isFinite(a) && a > 0 ? apiVal : prevVal ?? apiVal;
  };
  return {
    ...apiRow,
    lastTradedPrice: keepLtp ? prevRow.lastTradedPrice : apiRow.lastTradedPrice,
    buyPrice: keepBid ? prevRow.buyPrice : apiRow.buyPrice,
    sellPrice: keepAsk ? prevRow.sellPrice : apiRow.sellPrice,
    open: numOrPrev(apiRow.open, prevRow.open),
    high: numOrPrev(apiRow.high, prevRow.high),
    low: numOrPrev(apiRow.low, prevRow.low),
    close: numOrPrev(apiRow.close, prevRow.close),
    volume: numOrPrev(apiRow.volume, prevRow.volume),
    changeAmount: prevRow.changeAmount,
    changePercent: prevRow.changePercent,
    changeAmountColor: prevRow.changeAmountColor,
    colorLTP: prevRow.colorLTP,
    colorBid: prevRow.colorBid,
    colorAsk: prevRow.colorAsk,
    isUpdated: prevRow.isUpdated,
  };
}

/** Legacy-compatible: socket `Script` almost always equals `displayName` (matches old fxbazzar_frontend Map keys). */
function getMarketWatchMapKey(x, idx) {
  if (x == null) return idx != null ? `mw:${idx}` : "";
  const dn = String(x.displayName ?? "").trim();
  if (dn) return dn;
  const k = getItemKey(x);
  return k || (idx != null ? `mw:${idx}` : "");
}

/** Index list: displayName first (same as old UI), then instrument token — avoids empty-key Map collapse. */
function getScriptIndexMapKey(x, idx) {
  if (x == null) return idx != null ? `idx:${idx}` : "";
  const dn = String(x.displayName ?? "").trim();
  if (dn) return dn;
  const tok =
    x.instrumentToken ??
    x.instrument_token ??
    x.InstrumentToken ??
    x.Token ??
    x.token ??
    x.securityId ??
    x.SecurityId;
  if (tok != null && String(tok).trim() !== "") return `tok:${String(tok).trim()}`;
  const k = getItemKey(x);
  return k || (idx != null ? `idx:${idx}` : "");
}

const initialState = {
  sortMarketWatchArr: [],
  isMarketWatchUpdated: false,
  multipleSelectedScriptData: [],
  marketWatchList: new Map(),
  filteredMarketWatchList: new Map(),
  scriptIndexList: new Map(),
  nseFutWatchList: [],
  mcxWatchList: [],
  nseOptWatchList: [],
  nseCdWatchList: [],
  marketWatchLoading: false,
  addMarketWatchLoading: false,
  errorAddMarketWatch: "",
  searchTextMarketWatch: "",
  selectedScriptOption: [],
  selectedExpiryDate: {},
  liveRateFromApiList: [],
  liveRateFromSocket: {},
  expiryDatesBySymbol: [],
  selectedSegment: {},
  deleteOrderObj: {},
  selectedScript: {},
  symbolNameString: "",
  symbolNameArray: [],
  symbolIndexScriptArray: [],
  oldSymbolNameArray: [],
  selectedFinalOrderFormObject: {},
  isOpenBottomOrderForm: false,
  scriptsBySegmentOfOrderList: [],
  isSelectedFinalOrderFormObject: false,
  scriptMarketWatchList: [],
  strikeOptionsList: [],
  scriptNewScriptString: "",
  oldScriptNewScriptString: "",
  currentTab: {},
  isAddedOrder: false,
  isAddedOrderLoading: false,
  marketWatchMainList: {},
  keyCode: {},
  callPutSelectedOption: {},
  lotQtySymbolObject: {},
  symbolInOrderList: [],
  symbolListWithExpiry: [],
  symbolListWithExpiryLoading: false,
  openProfileModal: false,
  isScriptRowSelected: false,
  formBuySellValues: {
    symbol: "", marketType: "", buySell: "BUY",
    market: { value: 5, label: "Market" },
    script: "", lot: "", qty: "", price: "", client: "",
  },
  buySellFormValues: {
    buySellType: "BUY", orderType: { value: 5, label: "Market" },
    lot: 0, qty: 0, price: 0, client: undefined, displayName: "",
  },
  addScriptFormValues: { marketType: "", symbol: "", expiry: "", callPut: "", strike: "" },
  addOrderLoading: false,
};

let ajaxWatchListRequest = null;
let ajaxExpiryDatesRequest = null;

export const loginApiKey = createAsyncThunk("/loginApi/trade", (data, { dispatch }) => {
  return new Promise((resolve, reject) => {
    fetchLoginKeyRawText()
      .then((text) => {
        if (text && text.split("\n") && text.split("\n").length > 2) {
          resolve(text.split("\n")[1].split("key:")[1]);
        } else reject(text);
      })
      .catch((error) => {
        if (error.response) dispatch(showMessage({ message: error.response.data.message }));
        else if (error.request) dispatch(showMessage({ message: error.request.message }));
        else dispatch(showMessage({ message: error.message }));
        reject(error);
      });
  });
});

export const getExpiryDatesBySymbol = createAsyncThunk(
  "/marketwatch/symbol/expiry/get",
  (data, { dispatch }) => {
    return new Promise((resolve, reject) => {
      let { symbol, segmentId } = data;
      const safeSymbol = encodeURIComponent(symbol);
      if (ajaxExpiryDatesRequest) {
        try { ajaxExpiryDatesRequest.cancel("CANCEL"); } catch {}
      }
      api
        .get(`market-watch/get-expiry-dates-by-symbol/${segmentId}/${safeSymbol}`)
        .then(({ data }) => {
          let { status, data: dataArray } = data;
          if (status === 200 && dataArray && dataArray.length > 0) resolve(dataArray);
          else reject(data);
        })
        .catch((error) => reject(error));
    });
  }
);

export const insertAllNewExpiryScript = createAsyncThunk(
  "/marketwatch/insert-all-new-expiry-script",
  ({ userId, segmentId }, { dispatch }) => {
    return new Promise((resolve, reject) => {
      let { userId } = getSessionData();
      api
        .get(`market-watch/Insert-all-new-expiry-script-in-watchwatch/${userId}/${segmentId}`)
        .then(({ data }) => {
          if (data?.status === 200) {
            dispatch(showMessage({ message: data.message, varient: "success" }));
            resolve(data);
          } else {
            dispatch(showMessage({ message: data.message }));
            reject(data);
          }
        })
        .catch((error) => {
          if (error.response) dispatch(showMessage({ message: error.response.data.message }));
          else if (error.request) dispatch(showMessage({ message: error.request.message }));
          else dispatch(showMessage({ message: error.message }));
          reject(error);
        });
    });
  }
);

export const getLiveRateFromApi = createAsyncThunk(
  "/liveRage/trade",
  ({ key, token, segment }) => {
    return new Promise((resolve, reject) => {
      fetchBbdTerminalJson(key, segment, token)
        .then((data) => resolve(data))
        .catch((error) => reject(error));
    });
  }
);

export const insertAllScriptInMarketWatch = createAsyncThunk(
  "/marketwatch/insert-all-script",
  ({ userId, segmentId }, { dispatch }) => {
    return new Promise((resolve, reject) => {
      api
        .get(`market-watch/Insert-all-script-in-watchwatch/${userId}/${segmentId}`)
        .then(({ data }) => {
          if (data?.status === 200) {
            dispatch(showMessage({ message: data.message, varient: "success" }));
            resolve(data);
          } else {
            dispatch(showMessage({ message: data.message }));
            reject(data);
          }
        })
        .catch((error) => {
          if (error.response) dispatch(showMessage({ message: error.response.data.message }));
          else if (error.request) dispatch(showMessage({ message: error.request.message }));
          else dispatch(showMessage({ message: error.message }));
          reject(error);
        });
    });
  }
);

export const getSymbolWithExpiry = createAsyncThunk(
  "/marketwatch/get-symbol-with-expiry",
  ({ segmentId, symbolName }) => {
    return new Promise((resolve, reject) => {
      let { userId } = getSessionData();
      api
        .get(`market-watch/get-all-script-by-user/${userId}/${segmentId}${symbolName ? `?Symbol=${symbolName}` : ""}`)
        .then(({ data }) => resolve(data?.data))
        .catch((error) => reject(error));
    });
  }
);

export const getSegmentWiseMarketWatch = createAsyncThunk(
  "/marketwatch/segmentwise",
  ({ segmentId }) => {
    return new Promise((resolve, reject) => {
      let { userId } = getSessionData();
      api
        .get(`market-watch/get-market-watches-by-user/${userId}/${segmentId}`)
        .then(({ data }) => { if (data.status === 200) resolve(data); })
        .catch((error) => reject(error));
    });
  }
);

/** `get-index-script` may return a bare array, `{ data: [] }`, or `{ records: [] }`. */
function normalizeIndexScriptPayload(payload) {
  if (payload == null) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.records)) return payload.records;
  if (Array.isArray(payload.data)) return payload.data;
  if (payload.data && Array.isArray(payload.data.records)) return payload.data.records;
  if (payload.data && Array.isArray(payload.data.data)) return payload.data.data;
  return [];
}

export const getIndexScript = createAsyncThunk("/marketwatch/indexscript", () => {
  return new Promise((resolve, reject) => {
    api
      .get("script/get-index-script")
      .then(({ data }) => {
        if (data?.status === 200) {
          resolve(normalizeIndexScriptPayload(data.data));
        } else {
          errorLog("Index script non-200:", data?.message || data?.status);
          reject(data || new Error("get-index-script failed"));
        }
      })
      .catch((error) => {
        errorLog("Index script error:", error?.message);
        reject(error);
      });
  });
});

export const getMarketWatchList = createAsyncThunk(
  "/marketwatch/market-watch-list",
  (data, { dispatch, getState }) => {
    return new Promise((resolve, reject) => {
      const { isRefresh = false } = data || {};
      const { marketWatchList = new Map() } = getState().marketWatch;
      if (marketWatchList?.size > 0 && !isRefresh) {
        resolve([...marketWatchList.values()]);
        return;
      }
      // Robust session reader — tries all known storage keys and field name variants
      const getRobustUserId = () => {
        const keys = ["fxbazzar_user", "fxbazzar_admin", "TradeSession"];
        for (const k of keys) {
          const raw = sessionStorage.getItem(k);
          if (!raw) continue;
          try {
            const s = k === "TradeSession" ? JSON.parse(window.atob(raw)) : JSON.parse(raw);
            const id = s.userId || s.UserId || s.user_id || s.userid || s.id || s.ID;
            if (id) return id;
          } catch {}
        }
        // also try localStorage
        for (const k of keys) {
          const raw = localStorage.getItem(k);
          if (!raw) continue;
          try {
            const s = k === "TradeSession" ? JSON.parse(window.atob(raw)) : JSON.parse(raw);
            const id = s.userId || s.UserId || s.user_id || s.userid || s.id || s.ID;
            if (id) return id;
          } catch {}
        }
        return null;
      };
      const sessionObj = getSessionData() || {};
      const userId =
        getRobustUserId() ||
        sessionObj.userId ||
        sessionObj.UserId ||
        sessionObj.id ||
        sessionObj.ID ||
        null;
      
      if (!userId) {
        errorLog("[MW slice] No userId in session — skip network; keep existing watchlist if any");
        const cur = getState().marketWatch?.marketWatchList;
        if (cur instanceof Map && cur.size > 0) {
          resolve([...cur.values()]);
          return;
        }
        resolve([]);
        return;
      }
      api
        .get(`/market-watch/get-market-watches-by-user/${userId}`)
        .then(({ data = {} }) => {
          let list = [];
          if (Array.isArray(data?.data)) list = data.data;
          else if (Array.isArray(data?.data?.data)) list = data.data.data;
          else if (Array.isArray(data?.data?.records)) list = data.data.records;
          else if (Array.isArray(data?.records)) list = data.records;
          else if (Array.isArray(data?.result)) list = data.result;
          else if (Array.isArray(data?.items)) list = data.items;
          else if (Array.isArray(data)) list = data;
          list = applySavedWatchlistOrder(list, userId);
          resolve(list);
        })
        .catch((error) => {
          errorLog("[MW] watchlist API error:", error?.message);
          reject(error);
        });
    });
  }
);

export const addMarketWatch = createAsyncThunk(
  "marketwatch/add",
  (paramData, { dispatch, getState }) => {
    return new Promise((resolve, reject) => {
      const { marketType, symbol } = paramData || {};
      let { userId } = getSessionData();
      let newFinalObject = {};
      newFinalObject.createdBy = userId;
      newFinalObject.userId = userId;
      newFinalObject.id = 0;
      newFinalObject.segmentId = marketType.value;
      newFinalObject.symbol = symbol.value;
      newFinalObject.script = symbol.value;
      newFinalObject.displayName = symbol.value;

      const display_name = newFinalObject.displayName?.trim()?.toLowerCase();
      const symbol_name = newFinalObject.symbol?.trim()?.toLowerCase().split("-")[0];
      let script = newFinalObject.script?.split(" ")?.[0]?.trim()?.toLowerCase().split("-")[0];

      if (!display_name.includes(symbol_name) || symbol_name !== script) {
        reject();
        dispatch(showMessage({ message: "Please select script again and try again" }));
      } else {
        api
          .post("market-watch/save-market-watch", newFinalObject)
          .then((item) => {
            if (item.status === 200) {
              dispatch(showMessage({ message: item.data.message, varient: "success" }));
              resolve(item.data.data);
            } else reject(item);
          })
          .catch((error) => {
            const msg = legacyAxiosToastMessage(error) || "Could not add symbol to Market Watch";
            dispatch(showMessage({ message: msg, varient: /disabled|used\s*=\s*no/i.test(msg) ? "warning" : undefined }));
            reject(error);
          });
      }
    });
  }
);

export const deleteMarketWatch = createAsyncThunk(
  "/market-watch/delete",
  (payload, { dispatch }) => {
    return new Promise((resolve, reject) => {
      const { id, displayName } = payload;
      const safeId = encodeURIComponent(String(id ?? "").trim());
      if (!safeId) {
        dispatch(showMessage({ message: "Invalid watchlist id" }));
        reject(new Error("Invalid watchlist id"));
        return;
      }
      // Full URL shape: {API_BASE}/api/v1/market-watch/delete-market-watch/{id}
      api
        .delete(`market-watch/delete-market-watch/${safeId}`)
        .then((res) => {
          const data = res?.data ?? {};
          const httpOk = res?.status >= 200 && res?.status < 300;
          const st = data.status ?? data.Status;
          const ok =
            httpOk &&
            (st === 200 ||
              st === "200" ||
              data.success === true ||
              data.Success === true ||
              (st == null && data.success !== false));
          if (ok) {
            resolve({ resData: data, id, displayName });
          } else {
            const msg = data.message || data.Message || "Delete failed";
            dispatch(showMessage({ message: msg }));
            reject(data);
          }
        })
        .catch((error) => {
          const body = error?.response?.data;
          if (body?.message || body?.Message) {
            dispatch(showMessage({ message: body.message || body.Message }));
          } else if (error.response) {
            dispatch(showMessage({ message: error.response.statusText || "Delete failed" }));
          } else if (error.request) {
            dispatch(showMessage({ message: error.request.message }));
          } else {
            dispatch(showMessage({ message: error.message }));
          }
          reject(error);
        });
    });
  }
);

export const multipleDeleteMarketWatch = createAsyncThunk(
  "/market-watch/multidelete-marketwatch",
  (multiDeleteData, { dispatch }) => {
    return new Promise((resolve, reject) => {
      let deleteScript = multiDeleteData.map((item) => item.id);
      api
        .post("market-watch/multidelete-market-watch", deleteScript)
        .then(({ data }) => {
          if (data.status === 200) {
            resolve({ resData: data, deleteData: multiDeleteData });
          } else {
            dispatch(showMessage({ message: data.message }));
            reject(data);
          }
        })
        .catch((error) => {
          if (error.response) dispatch(showMessage({ message: error.response.data.message }));
          else if (error.request) dispatch(showMessage({ message: error.request.message }));
          else dispatch(showMessage({ message: error.message }));
          reject(error);
        });
    });
  }
);

export const getScriptsBySegmentOfOrder = createAsyncThunk(
  "/order/scriptBySymbol/get",
  (data, { dispatch }) => {
    return new Promise((resolve, reject) => {
      let { segmentId, symbol, date } = data;
      date = date.toISOString();
      api
        .get(`order/get-scripts-by-segment/${date}/${segmentId}${symbol ? `?symbol=${symbol}` : ""}`)
        .then(({ data }) => {
          let { status, data: dataArray } = data;
          if (status === 200 && dataArray && dataArray.length > 0) resolve(dataArray);
          else reject(data);
        })
        .catch((error) => reject(error));
    });
  }
);

export const getStrikeOptionsList = createAsyncThunk(
  "/marketWatch/stike/get",
  (data, { dispatch }) => {
    return new Promise((resolve, reject) => {
      let { segmentId, symbol, expiryDate, optType } = data;
      api
        .get(`market-watch/get-strike-prices-by-symbol/${segmentId}/${symbol}/${expiryDate}/${optType}`)
        .then(({ data }) => {
          let { status, data: dataArray } = data;
          if (status === 200 && dataArray && dataArray.length > 0) resolve(dataArray);
          else reject(data);
        })
        .catch((error) => reject(error));
    });
  }
);

export const addOrder = createAsyncThunk(
  "marketwatch/order/add",
  ({ data, liveDataItem: liveData, isAppView = false }, { dispatch, getState }) => {
    return new Promise((resolve, reject) => {
      const session = getSessionData();
      if (!isSessionTradingAllowed(session)) {
        dispatch(
          showMessage({
            message:
              "View-only login: you cannot place orders. Sign in with your trading password.",
          })
        );
        reject(new Error("View only"));
        return;
      }
      const baseQty = data.qty;
      let { userId, userIpFinal } = session || {};
      let executionType = 5;
      let dataObject = {};
      dataObject.createdBy = userId;
      dataObject.userId = data.client && data.client.value !== null ? Number(data.client.value) : 0;
      dataObject.id = 0;
      dataObject.deviceType = 1;
      dataObject.segmentId = liveData && liveData.segmentId;
      dataObject.script = liveData.script;
      dataObject.displayName = liveData.displayName;
      dataObject.userIp = userIpFinal;
      dataObject.orderType = data.buySell === "BUY" ? 1 : 2;
      dataObject.lot = data.lot;
      dataObject.qty = data.qty;
      dataObject.rate = data.price;
      dataObject.lowPrice = liveData.low;
      dataObject.mediumPrice = liveData.lastTradedPrice;
      dataObject.highPrice = liveData.high;
      if (data.market.value !== 5) {
        if (data.buySell === "BUY") {
          executionType = liveData.lastTradedPrice > Number(data.price) ? 1 : 2;
        } else {
          executionType = liveData.lastTradedPrice > Number(data.price) ? 4 : 3;
        }
      }
      dataObject.executionType = executionType;
      dataObject.symbol = liveData.symbol;
      if (dataObject.highPrice <= 0 || dataObject.mediumPrice <= 0 || dataObject.lowPrice <= 0) {
        dispatch(showMessage({ message: "High, Low or LTP Price should not be 0 or below" }));
        reject();
        return;
      }
      api
        .post("order/save-order", dataObject)
        .then((res) => {
          const body = res?.data;
          /* Loose == like fxbazzar_frontend — API may return numeric or string status */
          if (body != null && body.status == 200) {
            if (isAppView) {
              dispatch(showMessage({ message: body.message, varient: "successOrder" }));
            } else {
              dispatch(showMessage({ message: body.message, varient: "success" }));
            }
            // Market fill already wrote position on server — refresh PR immediately
            // (dynamic import avoids circular dep with Positionreport.slice).
            if (Number(data.market.value) === 5) {
              import("./Positionreport.slice")
                .then(({ getPositionReportList }) => {
                  const pr = getState().positionReport || {};
                  const filters = pr.positionReportFilterValue || {};
                  if (!filters || Object.keys(filters).length === 0) return;
                  dispatch(
                    getPositionReportList({
                      searchText: pr.positionReportSearchText || "",
                      pageNo: 1,
                      pageSize: pr.pageSizePositionReport || 10000,
                      appendData: false,
                      ...filters,
                    })
                  );
                })
                .catch(() => {});
            }
            resolve({
              executionType: Number(data.market.value),
              status: body.status,
              displayName: liveData.displayName,
              netQty: liveData.netQty,
              buySell: data.buySell,
              qty: baseQty ? parseInt(baseQty, 10) : 0,
            });
            return;
          }
          const failLine = body?.message ?? body?.Message;
          if (failLine != null && String(failLine).trim() !== "") {
            dispatch(showMessage({ message: String(failLine) }));
          }
          reject(body);
        })
        .catch((error) => {
          const msg = legacyAxiosToastMessage(error);
          dispatch(showMessage({ message: msg }));
          reject(error);
        });
    });
  }
);

export const getSymbolInOrderList = createAsyncThunk(
  "/marketwatch/script/get",
  ({ segmentId, optType }, { dispatch }) => {
    return new Promise((resolve, reject) => {
      api
        .get(`market-watch/get-symbols-by-segment/${segmentId}${optType ? `?symbol=${optType}` : ""}`)
        .then(({ data }) => {
          let { status, data: dataArray } = data;
          if (status === 200 && dataArray && dataArray.length > 0) resolve(dataArray);
          else reject(data);
        })
        .catch((error) => reject(error));
    });
  }
);

export const getLotQtyByDisplayName = createAsyncThunk(
  "/order/lotqty/get",
  ({ segmentId, displayname }, { dispatch }) => {
    return new Promise((resolve, reject) => {
      api
        .get(`market-watch/get-lot-and-qty-by-displayname/${segmentId}/${displayname}`)
        .then(({ data }) => {
          let { status } = data;
          if (status === 200) resolve(data.data);
          else reject(data);
        })
        .catch((error) => reject(error));
    });
  }
);

export const marketWatchSlice = createSlice({
  name: "marketWatch",
  initialState,
  reducers: {
    setSortMarketWatch:               (state, action) => { state.sortMarketWatchArr = action.payload; },
    setIsMarketWatchUpdated:          (state, action) => { state.isMarketWatchUpdated = action.payload; },
    setSearchTextMarketWatch:         (state, action) => { state.searchTextMarketWatch = action.payload; },
    setMarketWatchSelectedScript:     (state, action) => { state.selectedScriptOption = action.payload; },
    setSelectedExpiryDate:            (state, action) => { state.selectedExpiryDate = action.payload; },
    setSocketData:                    (state, action) => { state.liveRateFromSocket = action.payload; },
    setMarketWatchList: (state, action) => {
      const list = normalizeReduxListPayload(action.payload);
      let map = new Map();
      list?.forEach((x, i) => map.set(getMarketWatchMapKey(x, i), x));
      state.marketWatchList = map;
    },
    setFilteredMarketWatchList: (state, action) => {
      const list = normalizeReduxListPayload(action.payload);
      let map = new Map();
      list?.forEach((x, i) => map.set(getMarketWatchMapKey(x, i), x));
      state.filteredMarketWatchList = map;
    },
    setScriptIndexList: (state, action) => {
      const list = normalizeReduxListPayload(action.payload);
      let map = new Map();
      list?.forEach((x, i) => map.set(getScriptIndexMapKey(x, i), x));
      state.scriptIndexList = map;
    },
    setDeleteOrderObj:                (state, action) => { state.deleteOrderObj = action.payload; },
    setSelectedScript:                (state, action) => { state.selectedScript = action.payload; },
    setSymbolNameString:              (state, action) => { state.symbolNameString = action.payload; },
    setSymbolNameArray:               (state, action) => { state.symbolNameArray = action.payload; },
    setSymbolIndexScriptArray:        (state, action) => { state.symbolIndexScriptArray = action.payload; },
    setOldSymbolNameArray:            (state, action) => { state.oldSymbolNameArray = action.payload; },
    setAppendSymbolNameString:        (state, action) => { state.scriptNewScriptString = action.payload; },
    setOldScriptNewScriptString:      (state, action) => { state.oldScriptNewScriptString = action.payload; },
    setSelectedFinalOrderFormObject:  (state, action) => { state.selectedFinalOrderFormObject = action.payload; },
    setIsOpenBottomOrderForm:         (state, action) => { state.isOpenBottomOrderForm = action.payload; },
    setSelectedSegment:               (state, action) => { state.selectedSegment = action.payload; },
    setScriptsBySegmentOfOrderList:   (state, action) => { state.scriptsBySegmentOfOrderList = action.payload; },
    setNseFutWatchList:               (state, action) => { state.nseFutWatchList = action.payload; },
    setNseOptWatchList:               (state, action) => { state.nseOptWatchList = action.payload; },
    setMcxWatchList:                  (state, action) => { state.mcxWatchList = action.payload; },
    setIsSelectedFinalOrderFormObject:(state, action) => { state.isSelectedFinalOrderFormObject = action.payload; },
    setNseCdWatchList:                (state, action) => { state.nseCdWatchList = action.payload; },
    setScriptMarketWatchList:         (state, action) => { state.scriptMarketWatchList = action.payload; },
    setExpiryDatesBySymbol:           (state, action) => { state.expiryDatesBySymbol = action.payload; },
    setStrikeOptionsList:             (state, action) => { state.strikeOptionsList = action.payload; },
    setCurrentTab:                    (state, action) => { state.currentTab = action.payload; },
    setAddScriptFormValues: (state, action) => {
      state.addScriptFormValues = { ...state.addScriptFormValues, ...action.payload };
    },
    setIsAddedOrderLoading:           (state, action) => { state.isAddedOrderLoading = action.payload; },
    searchMarketWatch: (state, action) => {
      let { currentTab, searchTextMarketWatch, dataArray } = action.payload;
      if (currentTab.value === 1)      state.nseFutWatchList = Search(searchTextMarketWatch, dataArray);
      else if (currentTab.value === 2) state.mcxWatchList    = Search(searchTextMarketWatch, dataArray);
      else if (currentTab.value === 3) state.nseOptWatchList = Search(searchTextMarketWatch, dataArray);
      else if (currentTab.value === 4) state.nseCdWatchList  = Search(searchTextMarketWatch, dataArray);
    },
    setIsAddedOrder:              (state, action) => { state.isAddedOrder = action.payload; },
    setMarketWatchMainList:       (state, action) => { state.marketWatchMainList = action.payload; },
    setKeyCode:                   (state, action) => { state.keyCode = action.payload; },
    setCallPutSelectedOption:     (state, action) => { state.callPutSelectedOption = action.payload; },
    setOpenProfileModal:          (state, action) => { state.openProfileModal = action.payload; },
    setMultipleSelectedScriptData:(state, action) => { state.multipleSelectedScriptData = action.payload; },
    setIsScriptRowSelected:       (state, action) => { state.isScriptRowSelected = action.payload; },
    setFormBuySellValues:         (state, action) => { state.formBuySellValues = action.payload; },
    setSymbolListWithExpiry:      (state, action) => { state.symbolListWithExpiry = action.payload; },
    setBuySellFormValues: (state, action) => {
      state.buySellFormValues = { ...state.buySellFormValues, ...action.payload };
    },
    clearBuySellFormValues: (state) => {
      state.buySellFormValues = JSON.parse(JSON.stringify(initialState.buySellFormValues));
    },
    /** Rebuild Map in new row order (same map keys / row refs for socket updates). */
    reorderMarketWatchListOrder: (state, action) => {
      const ordered = action.payload;
      if (!Array.isArray(ordered) || ordered.length === 0) return;
      const old = state.marketWatchList;
      if (!old || !(old instanceof Map) || old.size === 0) return;
      const idToKey = new Map();
      old.forEach((v, k) => {
        if (v?.id != null && !Number.isNaN(Number(v.id))) idToKey.set(Number(v.id), k);
      });
      const newMap = new Map();
      ordered.forEach((row) => {
        if (row == null) return;
        let mapKey = null;
        if (row.id != null && !Number.isNaN(Number(row.id))) {
          mapKey = idToKey.get(Number(row.id)) ?? null;
        }
        if (!mapKey) {
          for (const [k, v] of old.entries()) {
            if (v === row) {
              mapKey = k;
              break;
            }
          }
        }
        if (!mapKey) mapKey = getMarketWatchMapKey(row, null);
        newMap.set(mapKey, old.get(mapKey) ?? row);
      });
      old.forEach((v, k) => {
        if (!newMap.has(k)) newMap.set(k, v);
      });
      state.marketWatchList = newMap;
    },
  },
  extraReducers: (builder) => {
    builder
      // ─── getLiveRateFromApi ────────────────────────────────────────────────
      // ALL 3 cases are NO-OPS — live rates must NOT be stored in Redux.
      // Writing to liveRateFromApiList every 5 seconds causes every component
      // subscribed to marketWatch state to re-render → full UI blinking + freeze.
      // Live rates are handled purely in MarketWatch.js local state (setLiveRates).
      .addCase(getLiveRateFromApi.pending,   (state) => { /* no-op — do not touch Redux state */ })
      .addCase(getLiveRateFromApi.rejected,  (state) => { /* no-op — do not touch Redux state */ })
      .addCase(getLiveRateFromApi.fulfilled, (state, action) => { /* no-op — data handled in component local state */ })

      // ─── getIndexScript ───────────────────────────────────────────────────
      .addCase(getIndexScript.fulfilled, (state, action) => {
        const session = getSessionData?.() || {};
        const userId = session?.userId ?? session?.id ?? session?.UserId ?? session?.UserID ?? null;
        const cache = loadTickCache(userId);
        let map = new Map();
        const list = Array.isArray(action.payload) ? action.payload : [];
        list.forEach((x, i) => {
          const merged = bootstrapIndexRowFromApi(mergeRowWithTickCache(x, cache));
          map.set(getScriptIndexMapKey(merged, i), merged);
        });
        const newString = list?.map((v) => v.displayName);
        if (newString) state.symbolIndexScriptArray = newString || "";
        state.scriptIndexList = map;
        state.symbolNameString = newString;
      })

      // ─── getMarketWatchList ───────────────────────────────────────────────
      .addCase(getMarketWatchList.pending, (state) => {
        state.isMarketWatchUpdated = false;
        const list = state.marketWatchList;
        const empty =
          !list ||
          (list instanceof Map
            ? list.size === 0
            : Array.isArray(list)
              ? list.length === 0
              : typeof list === "object"
                ? Object.keys(list).length === 0
                : true);
        if (empty) state.marketWatchLoading = true;
      })
      .addCase(getMarketWatchList.rejected, (state) => {
        state.isMarketWatchUpdated = false;
        state.marketWatchLoading = false;
      })
      .addCase(getMarketWatchList.fulfilled, (state, action) => {
        const session = getSessionData?.() || {};
        const userId = session?.userId ?? session?.id ?? session?.UserId ?? session?.UserID ?? null;
        const cache = loadTickCache(userId);
        let list = action.payload || [];
        list = _.map(list, (o) => _.extend({ isCheck: o.isCheck ? o.isCheck : false }, o));
        const oldMap =
          state.marketWatchList instanceof Map ? state.marketWatchList : new Map();
        const prevById = new Map();
        oldMap.forEach((v) => {
          if (v?.id != null && v.id !== "") prevById.set(String(v.id), v);
        });
        const map = new Map();
        list.forEach((x, i) => {
          const key = getMarketWatchMapKey(x, i);
          const prev = x?.id != null ? prevById.get(String(x.id)) : null;
          const mergedPrev = mergeWatchlistRowWithPrevious(prev, x);
          map.set(key, mergeRowWithTickCache(mergedPrev, cache));
        });
        state.marketWatchList = map;
        state.isMarketWatchUpdated = false;
        state.marketWatchLoading = false;
      })

      // ─── addMarketWatch ───────────────────────────────────────────────────
      .addCase(addMarketWatch.pending, (state) => {
        state.addMarketWatchLoading = true;
        state.isMarketWatchUpdated = false;
      })
      .addCase(addMarketWatch.rejected, (state) => {
        state.isMarketWatchUpdated = false;
        state.addMarketWatchLoading = false;
      })
      .addCase(addMarketWatch.fulfilled, (state, action) => {
        let addedObject = _.omit(action.payload, ["createdBy"]);
        const data = new Map(state.marketWatchList);
        data.set(getMarketWatchMapKey(addedObject, null), addedObject);
        state.marketWatchList = data;
        state.addMarketWatchLoading = false;
        state.isMarketWatchUpdated = true;
      })

      // ─── deleteMarketWatch ────────────────────────────────────────────────
      .addCase(deleteMarketWatch.fulfilled, (state, action) => {
        state.isMarketWatchUpdated = true;
        const delId = action?.payload?.id;
        const newMap = new Map();
        state.marketWatchList.forEach((v, k) => {
          const vid = v?.id;
          const sameId =
            delId != null &&
            vid != null &&
            (Number(vid) === Number(delId) || String(vid) === String(delId));
          if (sameId) return;
          newMap.set(k, v);
        });
        state.marketWatchList = newMap;
      })

      // ─── multipleDeleteMarketWatch ────────────────────────────────────────
      .addCase(multipleDeleteMarketWatch.fulfilled, (state, action) => {
        if (action?.payload?.deleteData?.length > 0) {
          const multiDeleteData = [...action.payload.deleteData];
          let mwData  = new Map(state.marketWatchList.entries ? state.marketWatchList : []);
          let fmwData = new Map(state.filteredMarketWatchList.entries ? state.filteredMarketWatchList : []);
          multiDeleteData.forEach((item) => {
            const k1 = getMarketWatchMapKey(item, null);
            const k2 = getItemKey(item);
            mwData.delete(k1);
            fmwData.delete(k1);
            if (k2 && k2 !== k1) {
              mwData.delete(k2);
              fmwData.delete(k2);
            }
          });
          state.isMarketWatchUpdated = true;
          state.marketWatchList = mwData;
          state.filteredMarketWatchList = fmwData;
        }
      })

      // ─── addOrder ─────────────────────────────────────────────────────────
      .addCase(addOrder.pending, (state) => {
        state.isAddedOrderLoading = true;
        state.isAddedOrder = false;
        state.addOrderLoading = true;
      })
      .addCase(addOrder.rejected, (state) => {
        state.isAddedOrderLoading = false;
        state.isAddedOrder = false;
        state.addOrderLoading = false;
      })
      .addCase(addOrder.fulfilled, (state, action) => {
        const mwData  = new Map(state.marketWatchList.entries ? state.marketWatchList : []);
        const fmwData = new Map(state.filteredMarketWatchList.entries ? state.filteredMarketWatchList : []);
        const dn = action.payload.displayName;
        let mapKey = null;
        let obj = null;
        if (dn) {
          if (mwData.has(dn)) {
            mapKey = dn;
            obj = mwData.get(dn);
          } else {
            for (const [k, v] of mwData.entries()) {
              if (v && (v.displayName === dn || v.script === dn || v.symbol === dn)) {
                mapKey = k;
                obj = v;
                break;
              }
            }
          }
        }
        let selectScript = state.selectedScript;
        if (action.payload.executionType === 5 && obj != null && mapKey != null) {
          const delta = action.payload.buySell === "BUY" ? action.payload.qty : -action.payload.qty;
          const newNet = action.payload.netQty + delta;
          if (
            selectScript?.displayName === dn ||
            selectScript?.script === dn ||
            selectScript?.symbol === dn
          ) {
            selectScript = { ...selectScript, netQty: newNet };
          }
          const updated = { ...obj, netQty: newNet };
          mwData.set(mapKey, updated);
          if (fmwData.has(mapKey)) fmwData.set(mapKey, updated);
        }
        state.selectedScript = selectScript;
        state.marketWatchList = mwData;
        state.filteredMarketWatchList = fmwData;
        state.isAddedOrderLoading = false;
        state.isAddedOrder = true;
        state.addOrderLoading = false;
      })

      .addCase(getSymbolInOrderList.rejected,   (state) => { state.symbolInOrderList = []; })
      .addCase(getSymbolInOrderList.fulfilled,  (state, action) => { state.symbolInOrderList = action.payload; })
      .addCase(getExpiryDatesBySymbol.rejected,  (state) => { state.expiryDatesBySymbol = []; })
      .addCase(getExpiryDatesBySymbol.fulfilled, (state, action) => { state.expiryDatesBySymbol = action.payload; })
      .addCase(getScriptsBySegmentOfOrder.rejected,  (state) => { state.scriptsBySegmentOfOrderList = []; })
      .addCase(getScriptsBySegmentOfOrder.fulfilled, (state, action) => { state.scriptsBySegmentOfOrderList = action.payload; })
      .addCase(getStrikeOptionsList.rejected,  (state) => { state.strikeOptionsList = []; })
      .addCase(getStrikeOptionsList.fulfilled, (state, action) => { state.strikeOptionsList = action.payload; })
      .addCase(getLotQtyByDisplayName.rejected,  (state) => { state.lotQtySymbolObject = []; })
      .addCase(getLotQtyByDisplayName.fulfilled, (state, action) => { state.lotQtySymbolObject = action.payload; })
      .addCase(getSymbolWithExpiry.pending,    (state) => { state.symbolListWithExpiry = []; state.symbolListWithExpiryLoading = true; })
      .addCase(getSymbolWithExpiry.rejected,   (state) => { state.symbolListWithExpiry = []; state.symbolListWithExpiryLoading = false; })
      .addCase(getSymbolWithExpiry.fulfilled,  (state, action) => { state.symbolListWithExpiry = action.payload; state.symbolListWithExpiryLoading = false; });
  },
});

export const {
  setSortMarketWatch, setSymbolListWithExpiry, fetchMasterWatchRequest, fetchMasterWatchSuccess,
  setIsMarketWatchUpdated, setSearchTextMarketWatch, setMarketWatchSelectedScript,
  setSelectedExpiryDate, setSocketData, setMarketWatchList, setFilteredMarketWatchList,
  setDeleteOrderObj, setSelectedScript, setSymbolNameString, setSymbolNameArray,
  setSymbolIndexScriptArray, setOldSymbolNameArray, setOldScriptNewScriptString,
  setSelectedFinalOrderFormObject, setIsOpenBottomOrderForm, setSelectedSegment,
  setScriptsBySegmentOfOrderList, setSelectedScriptOrderFinal, setNseFutWatchList,
  setMcxWatchList, setNseOptWatchList, setNseCdWatchList, setIsSelectedFinalOrderFormObject,
  setScriptMarketWatchList, setAppendSymbolNameString, setExpiryDatesBySymbol,
  setStrikeOptionsList, setCurrentTab, searchMarketWatch, setIsAddedOrder,
  setMarketWatchMainList, setAddScriptFormValues, setIsAddedOrderLoading, setKeyCode,
  setCallPutSelectedOption, setOpenProfileModal, setMultipleSelectedScriptData,
  setIsScriptRowSelected, setFormBuySellValues, setBuySellFormValues, clearBuySellFormValues,
  setScriptIndexList,
  reorderMarketWatchListOrder,
} = marketWatchSlice.actions;

export default marketWatchSlice.reducer;